import { useLayoutEffect, useRef, useState } from 'react'
import { Link, Location } from 'react-router-dom'
import classNames from 'classnames'
import * as Tooltip from '@radix-ui/react-tooltip'

import { Body } from '@foxino/components-web'

import { SelfSizingHeading } from '@app/components/SelfSizingHeading/SelfSizingHeading'
import { RouteType } from '@app/config/router/navMenu.types'
import type { Subject } from '@app/data'
import { useTranslation } from '@app/locales'
import { checkUrlIsActive } from '@app/modules/common/utils'
import { languageToFlagIcon } from '@app/modules/common/utils/languageToFlagIcon'
import resolveTailwindClasses from '@app/utils/resolveTailwindClasses'

import { NavigationItem } from './NavigationItem'

type Props = RouteType & {
  isActive: boolean
  isExpanded: boolean
  location?: Location<any>
  subject?: Subject
  identificator?: string
}

export const ClassroomNavigationItem = ({
  to,
  isActive,
  isExpanded,
  label,
  identificator,
  testId,
  disabled,
  location,
  children,
  color,
  subject
}: Props) => {
  const { t } = useTranslation('common')
  const headingWrapperRef = useRef<HTMLDivElement>(null)
  const [parentWidth, setParentWidth] = useState<number>(0)

  const SubjectIcon = subject ? languageToFlagIcon[subject] : null

  useLayoutEffect(() => {
    if (headingWrapperRef.current) {
      setParentWidth(headingWrapperRef.current.offsetWidth)
    }
  }, [headingWrapperRef.current?.offsetWidth, isExpanded])

  return (
    <div className={classNames('flex w-full flex-col gap-1 rounded-[10px] bg-primary-700')}>
      <Link
        to={disabled ? '#' : to}
        data-testid={testId}
        className={resolveTailwindClasses(
          'justify-left relative flex max-h-[52px] min-h-[52px] w-full cursor-pointer items-center gap-2 rounded-lg border text-2xl',
          isActive
            ? 'rounded-lg border-white bg-primary-500 text-primary-100 shadow-sm'
            : 'border-transparent bg-primary-700 text-primary-300 transition-colors hover:bg-primary-600'
        )}
        style={color ? { background: color } : {}}
      >
        <Tooltip.Provider>
          <Tooltip.Root delayDuration={0}>
            <div className={classNames('flex w-full items-center', isExpanded ? 'flex-row' : 'flex-col')}>
              {!isExpanded && (
                <Tooltip.Trigger className="w-full">
                  <div
                    ref={headingWrapperRef}
                    className="relative mt-1 w-full overflow-hidden text-primary-200 transition-all"
                  >
                    <SelfSizingHeading
                      parentWidth={parentWidth}
                      defaultFontSize="sm"
                      weight="bold"
                      className="z-10 font-gelica"
                    >
                      {identificator || label.slice(0, 3).toUpperCase()}
                    </SelfSizingHeading>

                    {SubjectIcon && (
                      <div
                        className={classNames('mt-0.5 flex items-center justify-center', {
                          'mt-[-4px]': !isExpanded
                        })}
                      >
                        <SubjectIcon
                          className={classNames('rounded-sm', isExpanded ? 'size-5 w-[20px]' : 'size-3 w-[14px]')}
                        />
                        <Body size="xs" weight="bold" className="ml-1 text-primary-200">
                          {t(`subject.${subject}`)?.slice(0, 3).toUpperCase()}
                        </Body>
                      </div>
                    )}
                  </div>
                </Tooltip.Trigger>
              )}

              <Tooltip.Portal>
                <Tooltip.Content
                  sideOffset={20}
                  side="right"
                  className="z-50 rounded-[10px] bg-white px-[10px] py-2 text-black shadow-2xl"
                >
                  <Body size="md" weight="regular">
                    {label}
                  </Body>

                  <Tooltip.Arrow className="fill-white" />
                </Tooltip.Content>
              </Tooltip.Portal>

              {isExpanded && (
                <div className="flex w-full flex-row items-center justify-between gap-2 overflow-hidden">
                  <Body
                    className={classNames(
                      'flex-2 max-w-[90%] truncate font-gelica text-2xl text-primary-200 transition-all',
                      isActive && isExpanded && 'py-4'
                    )}
                    weight="bold"
                  >
                    {label}
                  </Body>
                </div>
              )}
            </div>
          </Tooltip.Root>
        </Tooltip.Provider>
      </Link>

      {isActive && (
        <div className="flex w-full flex-col items-start gap-0">
          {children?.map((route, index) => {
            const isActiveSubItem = location ? checkUrlIsActive(route, location) : isActive

            return (
              <div key={index} className="relative w-full overflow-hidden p-1">
                <NavigationItem
                  className={classNames('flex w-full', isExpanded ? 'justify-start' : 'justify-center', {
                    '!bg-primary-400': isActiveSubItem
                  })}
                  {...route}
                  iconClassName="size-5"
                  isActive={isActiveSubItem}
                  isExpanded={isExpanded}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
